import { z } from 'zod';

/**
 * F36 — the recurring weekly/biweekly training rule for a club team (D7/S2).
 * A rolling-window generator materialises future occurrences as F16 bookings
 * (is_training_session=true). One-off ad-hoc sessions are created directly
 * (occurrence schedule_id=null). Reschedule of a single occurrence is
 * mutate-in-place (F17); editing the rule re-materialises future occurrences.
 *
 * Location: `subVenueId` set ⇒ the affiliated venue court (the booking sets
 * trainer_location_kind='affiliated_venue'); `locationText` only ⇒ off-court
 * (trainer_location_kind='trainer_own_location'). Exactly one must be present.
 */

export const TRAINING_CADENCE_VALUES = ['weekly', 'biweekly'] as const;
export type TrainingCadence = (typeof TRAINING_CADENCE_VALUES)[number];

export const TRAINING_SCHEDULE_STATUS_VALUES = ['active', 'paused'] as const;
export type TrainingScheduleStatus = (typeof TRAINING_SCHEDULE_STATUS_VALUES)[number];

/** Rolling-window horizon (days ahead) the generator materialises occurrences for. */
export const TRAINING_SCHEDULE_GENERATION_HORIZON_DAYS = 56; // 8 weeks

export type TrainingSchedule = {
  readonly id: string;
  readonly teamId: string;
  readonly trainerUserId: string;
  readonly b2bAccountId: string;
  readonly dayOfWeek: number; // 0–6 (Sunday=0)
  readonly startMinute: number; // 0–1439
  readonly endMinute: number; // 1–1440
  readonly cadence: TrainingCadence;
  readonly subVenueId: string | null;
  readonly locationText: string | null;
  readonly effectiveFrom: string; // ISO date
  readonly effectiveUntil: string | null;
  readonly status: TrainingScheduleStatus;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

const ScheduleCoreSchema = z
  .object({
    trainerUserId: z.string().uuid(),
    dayOfWeek: z.number().int().min(0).max(6),
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(1).max(1440),
    cadence: z.enum(TRAINING_CADENCE_VALUES),
    subVenueId: z.string().uuid().nullable().optional(),
    locationText: z.string().min(1).max(200).nullable().optional(),
    effectiveFrom: z.string().date(),
    effectiveUntil: z.string().date().nullable().optional(),
  })
  .strict()
  .refine((v) => v.endMinute > v.startMinute, {
    message: 'endMinute must be after startMinute',
    path: ['endMinute'],
  })
  .refine((v) => Boolean(v.subVenueId) || Boolean(v.locationText), {
    message: 'a session needs a place: set subVenueId or locationText',
    path: ['locationText'],
  });

/** POST /me/club/teams/:teamId/schedules */
export const CreateTrainingScheduleInputSchema = ScheduleCoreSchema;
export type CreateTrainingScheduleInput = z.infer<typeof CreateTrainingScheduleInputSchema>;

/** PATCH /me/club/teams/:teamId/schedules/:id (If-Match on updatedAt — S8). */
export const UpdateTrainingScheduleInputSchema = z
  .object({
    dayOfWeek: z.number().int().min(0).max(6).optional(),
    startMinute: z.number().int().min(0).max(1439).optional(),
    endMinute: z.number().int().min(1).max(1440).optional(),
    cadence: z.enum(TRAINING_CADENCE_VALUES).optional(),
    subVenueId: z.string().uuid().nullable().optional(),
    locationText: z.string().min(1).max(200).nullable().optional(),
    effectiveUntil: z.string().date().nullable().optional(),
    status: z.enum(TRAINING_SCHEDULE_STATUS_VALUES).optional(),
  })
  .strict();
export type UpdateTrainingScheduleInput = z.infer<typeof UpdateTrainingScheduleInputSchema>;

/** POST /me/club/teams/:teamId/sessions — a one-off ad-hoc training session. */
export const CreateOneOffSessionInputSchema = z
  .object({
    trainerUserId: z.string().uuid(),
    startAt: z.string().datetime({ offset: true }),
    endAt: z.string().datetime({ offset: true }),
    subVenueId: z.string().uuid().nullable().optional(),
    locationText: z.string().min(1).max(200).nullable().optional(),
  })
  .strict()
  .refine((v) => Boolean(v.subVenueId) || Boolean(v.locationText), {
    message: 'a session needs a place: set subVenueId or locationText',
    path: ['locationText'],
  });
export type CreateOneOffSessionInput = z.infer<typeof CreateOneOffSessionInputSchema>;
